import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import CLUB from '../../config/club.js'
import MemberPicker, { useMembersMap, memberName } from './MemberPicker.jsx'
import { Title, Card, Pill, Modal, Field, Err, NotifyBox, fmtDate } from './gui.jsx'
import { PURCHASE_RESULTS, watchDemoClubs, createDemo, returnDemo, deleteDemo, isOverdue } from '../../lib/demoClubs.js'

export default function GDemoClubs() {
  const [raw, setRaw] = useState({})
  const [tab, setTab] = useState('out')
  const [retFor, setRetFor] = useState(null)
  const [notifyFor, setNotifyFor] = useState(null)
  const members = useMembersMap()
  useEffect(() => { const off = watchDemoClubs(setRaw); return () => off() }, [])

  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), [raw])
  const out = rows.filter((r) => !r.returned)
  const overdue = out.filter(isOverdue)
  const returned = rows.filter((r) => r.returned)
  const list = tab === 'out' ? out : tab === 'overdue' ? overdue : returned

  return (
    <GShell>
      <section>
        <Title title="Demo Clubs" subtitle="Loaner check-out & return — with overdue reminders." />
        <div className="mt-3"><DemoForm /></div>
      </section>

      <div className="flex flex-wrap gap-2">
        {[['out', `Out ${out.length}`], ['overdue', `Overdue ${overdue.length}`], ['returned', `Returned ${returned.length}`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className="text-xs font-bold px-3 py-1.5 rounded-full"
            style={tab === k ? { background: 'var(--ge-primary)', color: '#fff' } : { background: 'rgba(0,0,0,0.05)', color: '#4B5563' }}>{l}</button>
        ))}
      </div>

      <section className="pb-4">
        {list.length === 0 ? <p className="text-sm text-gray-500">Nothing here.</p> : (
          <div className="grid md:grid-cols-2 gap-3">
            {list.map((d) => {
              const od = isOverdue(d)
              return (
                <Card key={d.id} style={od ? { borderColor: '#F9731655' } : {}}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{d.club}{d.manufacturer ? <span className="text-gray-400 font-normal"> · {d.manufacturer}</span> : null}</div>
                      <div className="text-sm text-gray-700">{memberName(members, d.memberId)}</div>
                    </div>
                    {d.returned ? <Pill bg="rgba(var(--ge-primary-rgb),0.14)" fg="var(--ge-primary)">Returned</Pill> : od ? <Pill bg="rgba(249,115,22,0.16)" fg="#C2410C">Overdue</Pill> : <Pill bg="rgba(37,99,235,0.12)" fg="#1D4ED8">Out</Pill>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    <span>Out {fmtDate(d.outDate)}</span>{d.dueDate ? <span>Due {fmtDate(d.dueDate)}</span> : null}
                    {d.serialNumber ? <span>S/N {d.serialNumber}</span> : null}{d.barcode ? <span>▐▌ {d.barcode}</span> : null}
                    {d.returned ? <span>Returned {fmtDate(d.returnedDate)} · {d.purchaseResult}</span> : null}
                  </div>
                  {d.notes ? <p className="text-xs text-gray-600 mt-1">{d.notes}</p> : null}
                  <div className="mt-3 flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    {!d.returned ? <button className="btn-primary text-xs" onClick={() => setRetFor(d)}>Return</button> : null}
                    {od ? <button className="btn-secondary text-xs" onClick={() => setNotifyFor(d)}>🔔 Remind</button> : null}
                    <button className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm(`Delete demo ${d.club}?`)) await deleteDemo(d.id) }}>Delete</button>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </section>

      {retFor ? <ReturnModal d={retFor} onClose={() => setRetFor(null)} /> : null}
      {notifyFor ? <RemindModal d={notifyFor} members={members} onClose={() => setNotifyFor(null)} /> : null}
    </GShell>
  )
}

function ReturnModal({ d, onClose }) {
  const [condition, setCondition] = useState(d.condition || 'Good')
  const [purchaseResult, setPR] = useState(d.purchaseResult || 'Pending')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const save = async () => { setBusy(true); setErr(''); try { await returnDemo(d.id, { condition, purchaseResult }, d.memberId, d.club); onClose() } catch (e) { setErr(e?.message || 'Failed'); setBusy(false) } }
  return (
    <Modal title={`Return — ${d.club}`} onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-2">
        <Field label="Condition on return"><input className="input w-full" value={condition} onChange={(e) => setCondition(e.target.value)} /></Field>
        <Field label="Purchase result"><select className="input w-full" value={purchaseResult} onChange={(e) => setPR(e.target.value)}>{PURCHASE_RESULTS.map((p) => <option key={p}>{p}</option>)}</select></Field>
      </div>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Mark Returned'}</button></div>
      <Err msg={err} />
    </Modal>
  )
}

function RemindModal({ d, members, onClose }) {
  const first = (memberName(members, d.memberId) || '').split(/\s+/)[0] || 'there'
  const msg = `Hi ${first}, this is a friendly reminder to return the ${d.club} demo club to the ${CLUB.name} pro shop${d.dueDate ? ` (was due ${fmtDate(d.dueDate)})` : ''}. Thanks!`
  return <Modal title="Overdue reminder" onClose={onClose}><NotifyBox message={msg} /></Modal>
}

function DemoForm() {
  const empty = { memberId: '', club: '', manufacturer: '', serialNumber: '', barcode: '', outDate: '', dueDate: '', notes: '' }
  const [f, setF] = useState(empty)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const submit = async () => { setBusy(true); setErr(''); try { await createDemo(f); setF(empty) } catch (e) { setErr(e?.message || 'Failed') } finally { setBusy(false) } }
  return (
    <Card>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: 'var(--ge-primary)' }}>Check Out Demo</div>
      <div className="mb-2"><MemberPicker value={f.memberId} onChange={(v) => setF((s) => ({ ...s, memberId: v }))} /></div>
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="input" placeholder="Club" value={f.club} onChange={set('club')} />
        <input className="input" placeholder="Manufacturer" value={f.manufacturer} onChange={set('manufacturer')} />
        <input className="input" placeholder="Serial number" value={f.serialNumber} onChange={set('serialNumber')} />
        <input className="input" placeholder="Barcode (optional)" value={f.barcode} onChange={set('barcode')} />
        <input className="input" type="date" value={f.outDate} onChange={set('outDate')} title="Out date (defaults today)" />
        <input className="input" type="date" value={f.dueDate} onChange={set('dueDate')} title="Due date" />
      </div>
      <input className="input mt-2 w-full" placeholder="Notes" value={f.notes} onChange={set('notes')} />
      <button className="btn-primary text-sm mt-2" onClick={submit} disabled={busy || !f.club.trim()}>{busy ? 'Adding…' : 'Check Out'}</button>
      <Err msg={err} />
    </Card>
  )
}
